import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChatConversation, ChatMessage } from '../data/types';

import { getConversation, listMessages, setMessageFeedback } from '../api/chat-api';
import { isRtlText } from '../lib/text-direction';
import { useChatStream } from './use-chat-stream';

interface UseChatConversationOptions {
  /** URL conversation id (from `useParams`). Undefined when at `/c`. */
  conversationId: string | undefined;
  /** Called when the server creates a fresh conversation (response to a /c-route send). */
  onConversationCreated?: (id: string) => void;
  /** Called when the server retitles the conversation (AI title after the 2nd message). */
  onConversationTitled?: (id: string) => void;
}

interface UseChatConversationResult {
  conversation: ChatConversation | null;
  /** The active branch — one message per tree depth, tagged for the navigator. */
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;
  sendMessage: (
    content: string,
    opts?: { gemSlug?: string; useShamela?: boolean },
  ) => Promise<void>;
  setFeedback: (messageId: string, value: 'positive' | 'negative' | null) => void;
  regenerate: (messageId: string, opts?: { useShamela?: boolean }) => Promise<void>;
  /** Edit a user message — branches the conversation and streams a fresh reply. */
  editMessage: (messageId: string, content: string) => Promise<void>;
  /** Flip the displayed branch at a fork (`delta` = -1 prev, +1 next). */
  navigateBranch: (message: ChatMessage, delta: number) => void;
  /** Local teardown only — the detached server generation completes and persists. */
  abort: () => void;
  /** Explicit user stop — aborts the server generation (partial answer persists). */
  stop: () => void;
}

// The fork key for tree roots (messages with `parentId === null`).
const ROOT_KEY = '__root__';

// Detached-generation recovery: how often to refetch a thread whose assistant
// message is still `streaming` server-side, and when to give up (a message can
// only stay `streaming` in the DB if the API process died mid-generation).
const GENERATION_POLL_MS = 4000;
const MAX_GENERATION_POLLS = 75; // ~5 minutes

// Generates an ephemeral id for optimistic messages — distinguishable from real
// server ids (which are 24-char hex Mongo ObjectIds).
function makeLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Stable sibling order: oldest first, sequence breaks createdAt ties.
function compareSiblings(a: ChatMessage, b: ChatMessage): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.sequence - b.sequence;
}

export function useChatConversation({
  conversationId,
  onConversationCreated,
  onConversationTitled,
}: UseChatConversationOptions): UseChatConversationResult {
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  // Holds *every* message of *every* branch. The list the UI renders is derived
  // below (`displayedMessages`) by walking the tree along the active branch.
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Fork key (`parentId ?? ROOT_KEY`) → chosen child id. Absent ⇒ newest child.
  const [branchSelection, setBranchSelection] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tracks the assistant-message we're currently filling so chunks know which
  // entry in `messages` to mutate. Plain ref — chunks fire faster than React
  // state can settle.
  const pendingAssistantIdRef = useRef<string | null>(null);
  // The in-flight request's effective `useShamela`, stamped onto the streaming
  // placeholder on `message_created` so the thinking phrases reflect what THIS
  // generation actually requested — never the ambient composer toggle (which
  // mislabels regenerate, mid-stream toggle flips, hero auto-send, and edits).
  // Set right before each stream call; recovery-after-refresh gets the server's
  // true value via `listMessages`.
  const pendingShamelaRef = useRef(false);
  // The in-flight request's question language (Arabic vs. not), stamped
  // alongside `pendingShamelaRef` — feeds the `onThinking` language guard
  // (spec §4): a `reasoning` detail snippet in a different language than an
  // Arabic question shows as label-only, never mixed-language text.
  const pendingArabicRef = useRef(false);
  // Set on `conversation_created`; signals the URL-update effect to fire AND
  // tells the next conversationId-change effect to skip a refetch (we already
  // have the data locally).
  const ownIdRef = useRef<string | null>(null);
  const onConversationCreatedRef = useRef(onConversationCreated);
  onConversationCreatedRef.current = onConversationCreated;
  const onConversationTitledRef = useRef(onConversationTitled);
  onConversationTitledRef.current = onConversationTitled;

  // Drop a pinned branch selection for one fork so its newest child (a freshly
  // streamed edit/regeneration) becomes visible.
  const clearFork = useCallback((forkKey: string): void => {
    setBranchSelection((prev) => {
      if (!(forkKey in prev)) return prev;
      const next = { ...prev };
      delete next[forkKey];
      return next;
    });
  }, []);

  const stream = useChatStream({
    onConversationCreated: ({ id, title }) => {
      ownIdRef.current = id;
      setConversation((prev) =>
        prev ?? {
          id,
          title,
          status: 'active',
          messageCount: 0,
          lastMessagePreview: null,
          lastMessageAt: null,
          gemId: null,
          gemSlug: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      );
      onConversationCreatedRef.current?.(id);
    },
    onConversationTitled: ({ id, title }) => {
      // Swap the placeholder title for the AI-generated one in the topbar; the
      // callback lets the layout refresh the sidebar list row too.
      setConversation((prev) => (prev ? { ...prev, title } : prev));
      onConversationTitledRef.current?.(id);
    },
    onMessageCreated: ({ id, role, sequence, parentId }) => {
      if (role === 'user') {
        // Replace the optimistic user message (id starts with "user-…") with the
        // real server id + tree parent so edits/feedback later target the right doc.
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.role === 'user' && m.id.startsWith('user-'));
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx]!, id, sequence, parentId, status: 'completed' };
          return next;
        });
        return;
      }
      // role === 'assistant' — seed a streaming placeholder under its tree parent.
      pendingAssistantIdRef.current = id;
      setMessages((prev) => [
        ...prev,
        {
          id,
          role: 'assistant',
          content: '',
          status: 'streaming',
          sequence,
          feedback: null,
          createdAt: new Date().toISOString(),
          parentId,
          shamelaUsed: pendingShamelaRef.current,
        },
      ]);
    },
    onThinking: ({ messageId, stage, detail }) => {
      if (!stage) return;
      // Language guard (spec §4): reasoning snippets in a language other than
      // the (Arabic) question show as label-only.
      const guarded =
        stage === 'reasoning' && detail && pendingArabicRef.current && !isRtlText(detail)
          ? undefined
          : detail;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, liveStage: { stage, ...(guarded ? { detail: guarded } : {}) } }
            : m,
        ),
      );
    },
    onChunk: ({ content }) => {
      const assistantId = pendingAssistantIdRef.current;
      if (!assistantId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content: m.content + content } : m,
        ),
      );
    },
    onDone: ({ messageId }) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, status: 'completed' } : m)),
      );
      pendingAssistantIdRef.current = null;
    },
    onError: ({ code, message }) => {
      const assistantId = pendingAssistantIdRef.current;
      const convId = conversation?.id ?? conversationId;
      // Connection dropped mid-answer (network blip, laptop sleep) — the
      // detached server generation keeps going and persists the answer, so
      // leave the message `streaming`; the poll effect below recovers it.
      if (code === 'NETWORK_ERROR' && assistantId && convId) {
        pendingAssistantIdRef.current = null;
        return;
      }
      setError(message);
      if (assistantId) {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, status: 'error' } : m)),
        );
      }
      pendingAssistantIdRef.current = null;
    },
  });

  // Load conversation + messages whenever the URL id changes — unless it's the
  // conversation we *just* created locally (its data is already in state).
  useEffect(() => {
    if (!conversationId) {
      setConversation(null);
      setMessages([]);
      setBranchSelection({});
      setError(null);
      ownIdRef.current = null;
      return;
    }
    if (ownIdRef.current === conversationId) {
      // We created this conversation in this session — keep the in-memory state.
      ownIdRef.current = null;
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const [{ conversation: conv, gemSlug }, msgs] = await Promise.all([
          getConversation(conversationId),
          listMessages(conversationId),
        ]);
        if (cancelled) return;
        // `gemSlug` arrives alongside (not inside) the conversation — merge it
        // so consumers can read the bound Gem off the conversation object.
        setConversation({ ...conv, gemSlug });
        setMessages(msgs);
        setBranchSelection({});
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message || 'Failed to load conversation');
        setConversation(null);
        setMessages([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Detached-generation recovery: the server finishes answers even after the
  // tab leaves or the connection drops. When this thread holds an assistant
  // message still `streaming` and no live stream is attached (came back
  // mid-generation, or the stream died with NETWORK_ERROR above), refetch on
  // an interval until the persisted answer lands. Capped: a message can only
  // stay `streaming` in the DB if the API died mid-generation.
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (!conversationId) return undefined;
    if (stream.isStreaming) return undefined;
    const inflight = messages.some((m) => m.role === 'assistant' && m.status === 'streaming');
    if (!inflight) {
      pollCountRef.current = 0;
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      pollCountRef.current += 1;
      if (pollCountRef.current > MAX_GENERATION_POLLS) {
        setMessages((prev) =>
          prev.map((m) =>
            m.role === 'assistant' && m.status === 'streaming' ? { ...m, status: 'error' } : m,
          ),
        );
        return;
      }
      try {
        const msgs = await listMessages(conversationId);
        if (!cancelled) setMessages(msgs);
      } catch {
        // Fetch failed — nudge state so the effect re-runs and retries.
        if (!cancelled) setMessages((prev) => prev.slice());
      }
    }, GENERATION_POLL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [conversationId, stream.isStreaming, messages]);

  // The active branch — walk the tree from the root, at each fork following the
  // pinned `branchSelection` (default: newest child). Each displayed message is
  // tagged with `branchKey` + `variantIndex`/`variantCount` for the navigator.
  const displayedMessages = useMemo<ChatMessage[]>(() => {
    if (messages.length === 0) return [];

    // Legacy fallback: a conversation whose messages predate `parentId` (every
    // message null) renders as a flat sequence-ordered thread. Migrated/new
    // conversations always have at least one parented message.
    if (messages.length > 1 && messages.every((m) => !m.parentId)) {
      return [...messages].sort((a, b) => a.sequence - b.sequence);
    }

    const childrenOf = new Map<string, ChatMessage[]>();
    for (const m of messages) {
      const key = m.parentId ?? ROOT_KEY;
      const list = childrenOf.get(key);
      if (list) list.push(m);
      else childrenOf.set(key, [m]);
    }
    for (const list of childrenOf.values()) list.sort(compareSiblings);

    const out: ChatMessage[] = [];
    const visited = new Set<string>();
    let key = ROOT_KEY;
    for (;;) {
      const siblings = childrenOf.get(key);
      if (!siblings || siblings.length === 0) break;
      const pinnedId = branchSelection[key];
      let idx = pinnedId ? siblings.findIndex((s) => s.id === pinnedId) : -1;
      if (idx === -1) idx = siblings.length - 1; // default: newest branch
      const chosen = siblings[idx]!;
      if (visited.has(chosen.id)) break; // defensive: cycle guard
      visited.add(chosen.id);
      out.push({
        ...chosen,
        branchKey: key,
        variantIndex: idx,
        variantCount: siblings.length,
      });
      key = chosen.id;
    }
    return out;
  }, [messages, branchSelection]);

  const sendMessage = useCallback(
    async (content: string, opts?: { gemSlug?: string; useShamela?: boolean }): Promise<void> => {
      const trimmed = content.trim();
      if (!trimmed) return;

      setError(null);
      const convId = conversation?.id ?? conversationId;
      // Continuing a conversation: the new message descends from the last
      // message on the active branch. A fresh conversation has no parent.
      const parentId =
        convId && displayedMessages.length > 0
          ? displayedMessages[displayedMessages.length - 1]!.id
          : undefined;

      // Optimistic user message — replaced with the real server message id when
      // the `message_created` event arrives.
      const optimisticId = makeLocalId('user');
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: 'user',
          content: trimmed,
          status: 'pending',
          sequence: prev.length,
          feedback: null,
          createdAt: new Date().toISOString(),
          parentId: parentId ?? null,
        },
      ]);

      pendingShamelaRef.current = opts?.useShamela ?? false;
      pendingArabicRef.current = isRtlText(trimmed);
      await stream.send({
        content: trimmed,
        conversationId: convId,
        gemSlug: opts?.gemSlug,
        useShamela: opts?.useShamela,
        parentId,
      });
    },
    [stream, conversation?.id, conversationId, displayedMessages],
  );

  // Optimistically set thumbs feedback on an assistant message; revert the one
  // message if the server rejects it. Clicking the active value clears it.
  const setFeedback = useCallback(
    (messageId: string, value: 'positive' | 'negative' | null): void => {
      const convId = conversation?.id ?? conversationId;
      if (!convId) return;
      const previous = messages.find((m) => m.id === messageId)?.feedback ?? null;
      if (previous === value) return;

      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback: value } : m)),
      );
      setMessageFeedback(convId, messageId, value).catch(() => {
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, feedback: previous } : m)),
        );
      });
    },
    [messages, conversation?.id, conversationId],
  );

  // Re-stream an assistant reply. The old answer is kept — the regenerate
  // stream's `message_created` adds the new one as a sibling (same tree parent),
  // and the UI flips between them with the ‹ n/total › navigator.
  const regenerate = useCallback(
    async (messageId: string, opts?: { useShamela?: boolean }): Promise<void> => {
      const convId = conversation?.id ?? conversationId;
      if (!convId) return;
      // The new variant joins this message's fork — unpin it so it shows.
      const target = messages.find((m) => m.id === messageId);
      if (target) clearFork(target.parentId ?? ROOT_KEY);
      setError(null);
      pendingShamelaRef.current = opts?.useShamela ?? false;
      // Regenerate carries no user text of its own — resolve the target
      // reply's parent user turn (the question actually being answered) for
      // the language guard.
      const parentUser = target ? messages.find((m) => m.id === target.parentId) : undefined;
      pendingArabicRef.current = parentUser ? isRtlText(parentUser.content) : false;
      await stream.regenerate({
        conversationId: convId,
        messageId,
        useShamela: opts?.useShamela,
      });
    },
    [stream, messages, conversation?.id, conversationId, clearFork],
  );

  // Edit a user message — creates a sibling user message (a new branch) with the
  // edited text; the server streams a fresh assistant reply under it.
  const editMessage = useCallback(
    async (messageId: string, content: string): Promise<void> => {
      const trimmed = content.trim();
      if (!trimmed) return;
      const convId = conversation?.id ?? conversationId;
      if (!convId) return;
      const original = messages.find((m) => m.id === messageId);
      if (!original || original.role !== 'user') return;

      setError(null);
      const forkKey = original.parentId ?? ROOT_KEY;

      // Optimistic sibling user message — replaced on `message_created`.
      const optimisticId = makeLocalId('user');
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: 'user',
          content: trimmed,
          status: 'pending',
          sequence: prev.length,
          feedback: null,
          createdAt: new Date().toISOString(),
          parentId: original.parentId,
        },
      ]);
      // Show the new branch (newest sibling) rather than a previously pinned one.
      clearFork(forkKey);

      // Edit sends no grounding flag (the server resolves conversation settings) —
      // mirror that for the placeholder stamp: inherit from the nearest previous
      // assistant reply in the displayed thread (the edited turn's predecessor).
      const editIdx = displayedMessages.findIndex((m) => m.id === messageId);
      let inherited = false;
      for (let i = (editIdx === -1 ? displayedMessages.length : editIdx) - 1; i >= 0; i -= 1) {
        const prev = displayedMessages[i]!;
        if (prev.role === 'assistant') {
          inherited = prev.shamelaUsed ?? false;
          break;
        }
      }
      pendingShamelaRef.current = inherited;
      // The edited text itself is the question being asked — use it directly.
      pendingArabicRef.current = isRtlText(trimmed);

      await stream.editMessage({ conversationId: convId, messageId, content: trimmed });
    },
    [stream, messages, displayedMessages, conversation?.id, conversationId, clearFork],
  );

  // Flip the displayed branch at the fork `message` sits in.
  const navigateBranch = useCallback(
    (message: ChatMessage, delta: number): void => {
      const key = message.branchKey ?? message.parentId ?? ROOT_KEY;
      const siblings = messages
        .filter((m) => (m.parentId ?? ROOT_KEY) === key)
        .sort(compareSiblings);
      const currentIdx =
        message.variantIndex ?? siblings.findIndex((s) => s.id === message.id);
      const nextIdx = currentIdx + delta;
      if (nextIdx < 0 || nextIdx >= siblings.length) return;
      setBranchSelection((prev) => ({ ...prev, [key]: siblings[nextIdx]!.id }));
    },
    [messages],
  );

  // Explicit user stop: aborts the server-side generation (which persists the
  // partial answer with finishReason 'cancelled') and freezes the local bubble
  // as-is — or drops it when nothing streamed yet.
  const stop = useCallback(() => {
    stream.stop();
    const assistantId = pendingAssistantIdRef.current;
    if (assistantId) {
      setMessages((prev) =>
        prev.flatMap((m) => {
          if (m.id !== assistantId) return [m];
          return m.content.length > 0 ? [{ ...m, status: 'completed' as const }] : [];
        }),
      );
      pendingAssistantIdRef.current = null;
    }
  }, [stream]);

  return {
    conversation,
    messages: displayedMessages,
    isLoading,
    isStreaming: stream.isStreaming,
    error,
    sendMessage,
    setFeedback,
    regenerate,
    editMessage,
    navigateBranch,
    abort: stream.abort,
    stop,
  };
}
